import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/lib/money';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Phone, MessageSquare, Mail, Loader2, Zap,
} from 'lucide-react';
import { fetchContactSessionHistory } from '@/lib/api';
import styles from './MyChargerContactsPage.module.css';

interface Props {
  chargerId: string;
  contactUserId: string;
  onNavigate: (path: string) => void;
}

/**
 * One contact's charging history at one charger — the owner's
 * "what has this person actually used, and what did I earn from
 * them" page.
 *
 * The money columns are deliberately two different numbers: what the
 * DRIVER paid (gross, their receipt) and what the OWNER earned (the
 * payout's resident slice). An uncleared payout shows "pending"
 * rather than $0, because a zero here would read as "this session
 * earned me nothing" when the money is simply still in flight.
 *
 * Contact actions (call / text / email) render only for pass-list
 * members: the server withholds a paying guest's phone and email, so
 * there is nothing to dial for them — see the two-tier PII rule on
 * GET /my-chargers/:id/contacts.
 */
export function MyChargerContactDetailPage({ chargerId, contactUserId, onNavigate }: Props) {
  const { t, i18n } = useTranslation('myChargersManage');
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-charger', chargerId, 'contact', contactUserId],
    queryFn: () => fetchContactSessionHistory(chargerId, contactUserId),
  });

  const contact = data?.contact;
  const totals = data?.totals;
  const name = contact?.name ?? t('contactCard.driverFallback');
  // Money in each amount's own currency (item 3); a total is one line per
  // currency, never a sum across them. An older Api sends USD only.
  const fmt = (cents: number, currency?: string | null): string => formatMoney(cents, currency, i18n.language);
  const totalLines = totals?.by_currency && totals.by_currency.length > 1
    ? totals.by_currency
    : [{
        currency: totals?.currency ?? 'USD',
        driver_paid_cents: totals?.driver_paid_cents ?? 0,
        connection_fee_cents: totals?.connection_fee_cents ?? 0,
        earned_cents: totals?.earned_cents ?? 0,
      }];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('back')}
          onClick={() => onNavigate(`/my-chargers/${chargerId}/contacts`)}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{name}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('contacts.loading')
              : totals
                ? t('contactDetail.subtitle', { count: totals.sessions })
                : ''}
          </p>
        </div>
      </header>

      {isLoading && (
        <div className={styles.list}>
          <div className={styles.cardSkeleton} />
          <div className={styles.cardSkeleton} />
        </div>
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('contacts.loadError')}
        </div>
      )}

      {!isLoading && !error && data && (
        <div className={styles.list}>
          {/* Contact actions — pass-list members only. */}
          {contact?.role === 'pass_list' && (contact.phone || contact.email) && (
            <div className={styles.actions}>
              {contact.phone && (
                <>
                  <a className={styles.actionBtn} href={`tel:${contact.phone}`}>
                    <Phone size={14} />
                    <span className={styles.actionLabel}>{t('contactCard.call')}</span>
                  </a>
                  <a className={styles.actionBtn} href={`sms:${contact.phone}`}>
                    <MessageSquare size={14} />
                    <span className={styles.actionLabel}>{t('contactCard.text')}</span>
                  </a>
                </>
              )}
              {contact.email && (
                <a className={styles.actionBtn} href={`mailto:${contact.email}`}>
                  <Mail size={14} />
                  <span className={styles.actionLabel}>{t('contactCard.email')}</span>
                </a>
              )}
            </div>
          )}

          {/* Totals */}
          <div className={styles.totalsCard}>
            <div className={styles.totalCell}>
              <span className={styles.totalLabel}>{t('contactDetail.totalEnergy')}</span>
              <span className={styles.totalValue}>{(totals?.kwh ?? 0).toFixed(1)} kWh</span>
            </div>
            <div className={styles.totalCell}>
              <span className={styles.totalLabel}>{t('contactDetail.totalPaid')}</span>
              <span className={styles.totalValue}>
                <MoneyLines values={totalLines.map((b) => fmt(b.driver_paid_cents, b.currency))} />
              </span>
            </div>
            <div className={styles.totalCell}>
              <span className={styles.totalLabel}>{t('contactDetail.totalEarned')}</span>
              <span className={styles.totalValueAccent}>
                <MoneyLines values={totalLines.map((b) => fmt(b.earned_cents, b.currency))} />
              </span>
            </div>
          </div>
          {(totals?.pending_sessions ?? 0) > 0 && (
            <p className={styles.pendingNote}>
              {t('contactDetail.pendingNote', { count: totals!.pending_sessions })}
            </p>
          )}

          {/* Session rows */}
          {data.sessions.length === 0 ? (
            <p className={styles.emptyDesc}>{t('contactDetail.noSessions')}</p>
          ) : (
            data.sessions.map((s) => (
              <article key={s.session_id} className={styles.sessionRow}>
                <div className={styles.sessionWhen}>
                  <Zap size={14} aria-hidden />
                  <div>
                    <p className={styles.sessionDate}>
                      {s.started_at ? formatDateTime(new Date(s.started_at), i18n.language) : '—'}
                    </p>
                    <p className={styles.sessionMeta}>
                      {s.kwh.toFixed(1)} kWh
                      {s.started_at && s.ended_at
                        ? ` · ${formatDuration(new Date(s.started_at), new Date(s.ended_at), t)}`
                        : ''}
                    </p>
                  </div>
                </div>
                <div className={styles.sessionMoney}>
                  <span className={styles.sessionPaid}>
                    {t('contactDetail.driverPaid', { amount: fmt(s.driver_paid_cents, s.currency) })}
                  </span>
                  {/* EVI's connection fee is inside what the driver paid and is
                      never the host's: named here so the gap between "Paid"
                      and "You earned" is explained (Tier 2). */}
                  {(s.connection_fee_cents ?? 0) > 0 && (
                    <span className={styles.sessionPaid}>
                      {t('contactDetail.connectionFee', { amount: fmt(s.connection_fee_cents ?? 0, s.currency) })}
                    </span>
                  )}
                  {s.earned_cents === null ? (
                    <span className={styles.sessionPending}>
                      <Loader2 size={11} /> {t('contactDetail.pending')}
                    </span>
                  ) : (
                    <span className={styles.sessionEarned}>
                      {t('contactDetail.youEarned', { amount: fmt(s.earned_cents, s.currency) })}
                    </span>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** Amounts in different currencies, one per line, never added up. */
function MoneyLines({ values }: { values: string[] }) {
  if (values.length === 1) return <>{values[0]}</>;
  return (
    <>
      {values.map((v) => (
        <span key={v} className={styles.moneyLine}>{v}</span>
      ))}
    </>
  );
}

function formatDateTime(d: Date, locale: string): string {
  return d.toLocaleString(locale, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatDuration(
  start: Date,
  end: Date,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000));
  if (mins < 60) return t('contactDetail.minutes', { count: mins });
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0
    ? t('contactDetail.hours', { count: h })
    : t('contactDetail.hoursMinutes', { hours: h, minutes: m });
}
